import bzxApi, { IUserYieldApi } from 'app-lib/apis/bzxApi'
import assetUtils, { Asset } from 'app-lib/assetUtils'
import { AnyProvider } from 'app-lib/providerUtils'
import { LendType } from 'bzx-common/src/domain/lendTypes'
import { BigNumber } from '@0x/utils'
import providerUtils from './providerUtils'

export type LendInfo = {
  tokenName: Asset
  supplyInterestRate: string
  address: string
  totalSupply: string
  totalBorrow: string
  availableLiquidity: string
}

/**
 * Source: blockchain
 */
async function getLendTokenInterestRates(
  provider: AnyProvider,
  assets: Asset[]
): Promise<BigNumber[]> {
  const { helperContract, chainId } = providerUtils.getDependenciesStrictRead(provider)
  const itokenAddresses = assetUtils.getITokenAddresses(chainId, assets)
  const interestRates = await helperContract.supplyInterestRate(itokenAddresses).callAsync()
  return interestRates.map((interestRate) => interestRate.dividedBy(10 ** 18))
}

const getMaxLendValue = async (provider: AnyProvider, lendType: LendType, asset: Asset) => {
  let maxLendAmount = new BigNumber(0)
  let maxTokenAmount = new BigNumber(0)
  let userLentBalance = new BigNumber(0)
  let freeSupply = new BigNumber(0)
  let tokenPrice = new BigNumber(0)
  if (lendType === LendType.LEND) {
    maxLendAmount = await providerUtils.getTokenBalanceOfUser(provider, asset)
  } else {
    const { contractsSource, helperContract, chainId } =
      providerUtils.getDependenciesStrict(provider)

    const assetContract = contractsSource.getITokenContract(asset)
    const itokenAddress = assetUtils.getITokenAddress(chainId, asset)
    const precision = assetUtils.getDecimals(provider.currentChainId, asset)

    tokenPrice = await assetContract.tokenPrice().callAsync()
    maxTokenAmount = await providerUtils.getITokenBalanceOfUser(provider, asset)
    freeSupply = (await helperContract.marketLiquidity([itokenAddress]).callAsync())[0].div(
      10 ** precision
    )
    userLentBalance = maxTokenAmount.multipliedBy(tokenPrice).dividedBy(10 ** 18)

    if (freeSupply.lt(userLentBalance)) {
      maxLendAmount = freeSupply
      maxTokenAmount = maxTokenAmount.multipliedBy(freeSupply).div(userLentBalance)
    } else {
      maxLendAmount = userLentBalance
    }
  }

  return { maxLendAmount, maxTokenAmount, tokenPrice, freeSupply, userLentBalance }
}

export interface IUserYield {
  asset: Asset
  /** User balance in underlying token, including profits */
  equity: BigNumber
  profit: BigNumber
  iTokenAddress: string
  iTokenPrice: BigNumber
  iTokenBalance: BigNumber
  userItokenPrice: BigNumber
}

/**
 * Note that values are already divided by the precision
 */
async function getLendProfits(provider: AnyProvider, assets: Asset[]): Promise<IUserYield[]> {
  const { helperContract, chainId, userAddress } = providerUtils.getDependenciesStrict(provider)
  const itokenAddresses = assetUtils.getITokenAddresses(chainId, assets)
  const balances = await helperContract.balanceOf(itokenAddresses, userAddress).callAsync()

  if (balances.length === 0) {
    return []
  }
  let userItokens: IUserYieldApi[] = []
  try {
    userItokens = await bzxApi.getUserItokenPrice({ chainId, address: userAddress })
  } catch (err) {
    console.error('Could not get user itoken price from api', err)
  }

  const yields = balances.reduce((acc, balance, i) => {
    if (balance.gt(0)) {
      const asset = assetUtils.wrapNativeAsset(chainId, assets[i])
      const iTokenAddress = assetUtils.getITokenAddress(chainId, asset).toLowerCase()
      const yieldFromApi = userItokens.find((yyield) => yyield.iTokenAddress === iTokenAddress)

      const decimals = assetUtils.getDecimals(chainId, assets[i])
      const iTokenBalance = balances[i].div(10 ** decimals)
      const equity = yieldFromApi
        ? iTokenBalance.times(yieldFromApi.iTokenPrice).dp(decimals)
        : new BigNumber(NaN)
      const iTokenPrice = new BigNumber(yieldFromApi ? yieldFromApi.iTokenPrice : NaN)
      const profit = yieldFromApi
        ? new BigNumber(yieldFromApi.iTokenPrice)
            .minus(yieldFromApi.userItokenPrice)
            .times(iTokenBalance)
        : new BigNumber(NaN)

      acc.push({
        asset,
        equity,
        profit,
        iTokenBalance,
        iTokenAddress,
        iTokenPrice,
        userItokenPrice: new BigNumber(yieldFromApi ? yieldFromApi.userItokenPrice : NaN),
      })
    }
    return acc
  }, [] as IUserYield[])

  return yields
}

/**
 * Get the interest rate change if user adds or removes liquidity.
 * @param amount (in decimals)
 */
async function nextSupplyInterestRate(provider: AnyProvider, asset: Asset, amount: BigNumber) {
  const { chainId, contractsSource } = providerUtils.getDependenciesStrictRead(provider)
  const decimals = assetUtils.getDecimals(chainId, asset)
  const iToken = contractsSource.getITokenContract(asset)
  return (await iToken.nextSupplyInterestRate(amount.times(10 ** decimals)).callAsync()).div(
    10 ** 18
  )
}

export default {
  getLendProfits,
  getLendTokenInterestRates,
  getMaxLendValue,
  nextSupplyInterestRate,
}
